import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { useSessionStore } from '../auth/session-store';
import type { DeliveryNote } from '../delivery-notes/api';
import { signatureQueue, syncPending } from './deliveries-repository';
import { createQueueController, type QueueSnapshot } from './queue-controller';
import type { QueuedSignature } from './signature-queue';

type SyncedListener = (notes: DeliveryNote[]) => void;

const syncedListeners = new Set<SyncedListener>();

/** Called after queued signatures reached the server, so screens can reload. */
export function onSignaturesSynced(listener: SyncedListener): () => void {
  syncedListeners.add(listener);

  return () => syncedListeners.delete(listener);
}

/** One outbox per app: the screens read it, the runner keeps it moving. */
export const queueController = createQueueController({
  list: signatureQueue.list,
  sync: syncPending,
  dismiss: signatureQueue.dismiss,
  getUserId: () => useSessionStore.getState().session?.userId ?? null,
  onSynced: (notes) => syncedListeners.forEach((listener) => listener(notes)),
});

export interface SignatureQueueView extends QueueSnapshot {
  /** The queued item of each note (pending or problem), by note id. */
  byNote: Map<string, QueuedSignature>;
  syncNow: () => Promise<void>;
  dismiss: (noteId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useSignatureQueue(): SignatureQueueView {
  const snapshot = useSyncExternalStore(queueController.subscribe, queueController.getSnapshot);
  useEffect(() => {
    void queueController.refresh();
  }, []);

  const byNote = useMemo(() => new Map(snapshot.items.map((item) => [item.noteId, item])), [snapshot.items]);

  return {
    ...snapshot,
    byNote,
    syncNow: queueController.syncNow,
    dismiss: queueController.dismiss,
    refresh: queueController.refresh,
  };
}
